// @ts-strict-ignore
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootStoreState } from 'mytours-read-common'
import { IonContent, IonGrid, IonRow } from '@ionic/react'
import Button from 'mobile_web/modules/common/components/Button'
import Modal from 'mobile_web/modules/common/components/modal'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'
import MapboxMap from 'mobile_web/modules/map/components/MapboxMap'
import { defaultMapStyle } from 'mobile_web/modules/map/functions'
import { getSocialLink, linksData, onLinkAction } from '../../../functions'
import { MapContainer, MapControls } from 'mobile_web/modules/map/types'
import { getCurrentLanguage, getDirMode, isRTL } from 'mobile_web/modules/platform'
import { ScreenSection, MapContainer as Container, loadMediaItem, Screen, MediaItem } from 'mytours-read-common'
import { GetScreensShowResponseSections, MapItem, MapRoute } from 'mytours-read-api-typescript'
import strings from '../../../../../strings'
import AccessModal from 'mobile_web/modules/common/components/access-modal'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import FullscreenMedia from '../FullscreenMedia'
import { sendEvent, EventActionsEnum } from 'mobile_web/modules/logging/analytics'
import FullscreenAr from '../FullscreenAr'

interface OwnProps extends MapContainer {
	showMap: boolean
	section: ScreenSection
	toggleMap: () => void
	collectionId?: number
	screen: Screen
}

type Props = Container.Actions & Container.Props & OwnProps

const MapModal: React.FC<Props> = ({
	showMap,
	section,
	toggleMap,
	containerType,
	containerId,
	loadMapRoutes,
	mapRoutes,
	screen,
	collectionId,
}) => {
	const dispatch = useDispatch()
	const [mapModalDidPresent, setMapModalDidPresent] = useState<boolean>(false)
	const [showMyPosition, setShowMyPosition] = useState<boolean>(false)
	const [showMediaModal, setShowMediaModal] = useState<boolean>(false)
	const [modalPresented, setModalPresented] = useState<boolean>(false)
	const [selectedMediaItemId, setSelectedMediaItemId] = useState<number>()
	const [selectedArMediaItemId, setSelectedArMediaItemId] = useState<number>()
	const [arModalPresented, setArModalPresented] = useState<boolean>(false)
	const [glbUrl, setGlbUrl] = useState<string>()
	const [usdzUrl, setUsdzUrl] = useState<string>()
	const [arFilename, setArFilename] = useState<string>()
	const mediaItems = useSelector((state: RootStoreState) => state.mediaitems.mediaItems)

	const isMultipleLocationsMap = section.map_type === GetScreensShowResponseSections.MapTypeEnum.MultipleLocations

	const openModal = (mapItem: MapItem): void => {
		const type = GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		const subtype = mapItem.item_subtype.split('_').reverse()[0] as GetCollectionItemsIndexResponseData.ItemSubtypeEnum
		NavigationService.pushModalWindow(
			type as GetCollectionItemsIndexResponseData.ItemTypeEnum,
			subtype,
			mapItem.item_id,
			(mapItem as any).item_version,
		)
	}

	useEffect(() => {
		if (section) {
			loadMapRoutes(section, MapRoute.LinkedTypeEnum.StorySection, getCurrentLanguage())
		}

		document.body.classList.add('map-modal-opened')

		return () => {
			document.body.classList.remove('map-modal-opened')
		}
	}, [])

	useEffect(() => {
		if (!section && !isMultipleLocationsMap) {
			return
		}

		const mapItemsWithMedia = section?.map_items?.filter(item => item.item_type === MapItem.ItemTypeEnum.MediaItem)

		if (mapItemsWithMedia?.length > 0) {
			mapItemsWithMedia.map(item => {
				dispatch(
					loadMediaItem({ id: item.item_id, version: (item as any).item_version, language: getCurrentLanguage() }),
				)
			})
		}
	}, [section])

	const toggleFullscreenMediaModal = (): void => {
		if (!showMediaModal) {
			sendEvent(EventActionsEnum.OpenLightbox, {})
		}

		setShowMediaModal(!showMediaModal)
	}

	const handleOpenAr = async (mediaItem: MediaItem) => {
		const { usdz, glb } = mediaItem
		setGlbUrl(glb?.original_url)
		setUsdzUrl(usdz?.original_url)
		setArFilename(glb?.filename || usdz?.filename)
	}

	useEffect(() => {
		if (selectedArMediaItemId) {
			handleOpenAr(mediaItems.get(selectedArMediaItemId).item)
		}
	}, [selectedArMediaItemId])

	const handleClickMarker = (item: MapItem): void => {
		if (!item) {
			return
		}
		if (item.link_type !== MapItem.LinkTypeEnum.Internal) {
			const data = linksData[item.link_type]
			const url = item.url || getSocialLink(item.link_text, data.link)
			window.open(url, '_blank')
		}
		if (item.link_type === MapItem.LinkTypeEnum.Internal && item.item_type !== MapItem.ItemTypeEnum.MediaItem) {
			onLinkAction(
				item.item_subtype,
				item.item_id,
				item.version,
				() => openModal(item),
				() => toggleMap(),
			)
		}

		if (item.item_type === MapItem.ItemTypeEnum.MediaItem && (item.item_subtype as string) === 'media_item_ar') {
			setSelectedArMediaItemId(item.item_id)
			return
		}

		if (item.item_type === MapItem.ItemTypeEnum.MediaItem) {
			setSelectedMediaItemId(item.item_id)
			toggleFullscreenMediaModal()
		}
	}

	const renderMapControls = ({ showAllBounds, getMyPosition }: MapControls): JSX.Element => (
		<IonRow className="map-controls-container" dir={getDirMode()}>
			<div className="map-controls-row map-controls-exit">
				<Button ariaLabel={strings.close} className="map-controls-exit-button" onClick={toggleMap}>
					{!isRTL() && <i className="fal fa-chevron-left" />}
					<span>{strings.close}</span>
					{isRTL() && <i className="fal fa-chevron-right" />}
				</Button>
			</div>
			<div className="map-controls-row">
				<Button onClick={showAllBounds} ariaLabel={strings.center_map}>
					<i className="fal fa-draw-square" />
				</Button>
				{/* TODO: uncomment when develop in app directions for mobile web */}
				{/* {section?.directions_enabled && (
					<Button onClick={() => setShowMyPosition(true)} ariaLabel={strings.directions}>
						<FontAwesomeIcon icon={faDirections} />
					</Button>
				)} */}
				<Button onClick={() => setShowMyPosition(true)} ariaLabel={strings.my_location}>
					<i className="fal fa-location-arrow" />
				</Button>
				{showMyPosition && (
					<AccessModal
						types={['location']}
						onCheckDone={() => {
							getMyPosition()
							setShowMyPosition(false)
						}}
					/>
				)}
			</div>
		</IonRow>
	)

	const onModalDidPresent = (): void => setModalPresented(true)

	const onModalDidDismiss = (): void => {
		setShowMediaModal(false)
		setModalPresented(false)
	}

	const onArModalDidPresent = (): void => setArModalPresented(true)

	const onArModalDidDismiss = (): void => {
		setArModalPresented(false)
		setUsdzUrl(undefined)
		setGlbUrl(undefined)
		setArFilename(undefined)
	}

	const renderFullscreenMediaModal = (): JSX.Element => (
		<Modal
			isOpen={showMediaModal}
			cssClass="media-modal"
			showBackdrop={true}
			onDidDismiss={onModalDidDismiss}
			onDidPresent={onModalDidPresent}
		>
			{modalPresented && (
				<FullscreenMedia
					mediaData={[mediaItems.get(selectedMediaItemId)?.item]}
					onBack={() => toggleFullscreenMediaModal()}
					section={section}
					screen={screen}
					collectionId={collectionId}
				/>
			)}
		</Modal>
	)

	const renderArModal = (): JSX.Element => (
		<Modal
			isOpen={Boolean(glbUrl) || Boolean(usdzUrl)}
			cssClass="media-modal"
			showBackdrop={true}
			onDidDismiss={onArModalDidDismiss}
			onDidPresent={onArModalDidPresent}
		>
			{arModalPresented && (
				<FullscreenAr
					usdz={usdzUrl}
					glb={glbUrl}
					filename={arFilename}
					onClose={() => {
						setGlbUrl(undefined)
						setUsdzUrl(undefined)
					}}
				/>
			)}
		</Modal>
	)

	return (
		<Modal
			isOpen={showMap}
			cssClass="map-modal location"
			onDidPresent={(): void => setMapModalDidPresent(true)}
			onDidDismiss={(): void => setMapModalDidPresent(false)}
		>
			<IonContent>
				<IonGrid className="map-modal-content" data-cy="fullscreen-location-container">
					<IonRow className="map-row">
						{mapModalDidPresent ? (
							<MapboxMap
								items={isMultipleLocationsMap ? section.map_items : [section as any]}
								currentItemIndex={0}
								mapStyle={`mapbox://styles/${defaultMapStyle}`}
								defaultZoom={16}
								containerType={containerType}
								containerId={containerId}
								boundAll={isMultipleLocationsMap}
								mapRoutes={mapRoutes}
								mapControls={renderMapControls}
								onSetActiveItem={handleClickMarker}
								isItemsLoaded={true}
								mediaItems={mediaItems}
								loadMediaItem={(id, version, language) => dispatch(loadMediaItem({ id, version, language }))}
							/>
						) : (
							<ImagePlaceholder style={{ width: '100vw', height: '100dvh' }} />
						)}
					</IonRow>
				</IonGrid>
			</IonContent>
			{showMediaModal && renderFullscreenMediaModal()}
			{(glbUrl || usdzUrl) && renderArModal()}
		</Modal>
	)
}

const MapModalComponent = Container.default(MapModal)
export default MapModalComponent as any
